import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ChevronDown, Globe2 } from 'lucide-react'

import { normalizeUiLang } from '@/i18n'
import { useTenantUILanguages } from '@/shared/hooks/useTenantUILanguages'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { cn } from '@/shared/lib/utils'

type LangSwitcherProps = {
  compact?: boolean
  fullWidth?: boolean
  variant?: 'dropdown' | 'drawer'
  className?: string
}

/**
 * 语言切换 — header / drawer 均用 Radix DropdownMenu（Portal）；
 * drawer 内自底部向上展开，避免列表被抽屉裁切。
 */
export function LangSwitcher({
  compact = false,
  fullWidth = false,
  variant = 'dropdown',
  className,
}: LangSwitcherProps) {
  const { t, i18n } = useTranslation()
  const { langs } = useTenantUILanguages()
  const current = normalizeUiLang(i18n.resolvedLanguage ?? i18n.language)
  const currentLabel = langs.find((l) => l.code === current)?.label ?? langs[0]?.label ?? '中文'
  const inDrawer = variant === 'drawer'

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      aria-haspopup="listbox"
      className={cn(
        !compact && 'portal-lang',
        'h-auto gap-1 font-normal',
        fullWidth && 'w-full justify-between',
        inDrawer && fullWidth && 'min-h-[44px] px-3 text-sm',
        compact &&
          'h-9 rounded-full border border-white/[0.08] bg-white/[0.04] px-3 text-[13px] text-zinc-200 hover:border-indigo-500/35 hover:bg-white/[0.06]',
        className,
      )}
    >
      <Globe2 size={14} className="shrink-0 text-zinc-400" aria-hidden />
      <span className={clsx('truncate', fullWidth ? 'flex-1 text-left' : 'max-w-[5.5rem]')}>
        {currentLabel}
      </span>
      <ChevronDown
        size={14}
        className="shrink-0 text-zinc-500 transition-transform duration-200 group-data-[state=open]/lang-trigger:rotate-180"
        aria-hidden
      />
    </Button>
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div
          className={cn(
            'group/lang-trigger relative',
            fullWidth && 'w-full',
            compact &&
              'rounded-full data-[state=open]:[&_button]:border-indigo-500/45 data-[state=open]:[&_button]:bg-indigo-500/10 data-[state=open]:[&_button]:text-zinc-100',
          )}
        >
          {trigger}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={inDrawer ? 'top' : 'bottom'}
        align={fullWidth ? 'start' : 'end'}
        sideOffset={inDrawer ? 8 : 4}
        className={cn(
          'portal-lang-dropdown z-[70] w-44',
          fullWidth && 'min-w-[var(--radix-dropdown-menu-trigger-width)]',
        )}
      >
        <DropdownMenuLabel>{t('common.changeLanguage')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={current} onValueChange={(code) => void i18n.changeLanguage(code)}>
          {langs.map((lang) => (
            <DropdownMenuRadioItem key={lang.code} value={lang.code} className="min-h-9">
              {lang.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
